import React, { useMemo, useCallback, useState } from 'react';
import { TextField as TPATextField, BoxSelectionBoxSize, BoxSelectionSelectionType } from 'wix-ui-tpa/cssVars';
import { useFieldAttributes } from '../../services/field-attributes';
import { FieldDescriptionWUT } from '../field-description';
import { FieldLabelWUT } from '../field-label';
import { FieldErrorWUT } from '../field-error';
import { getOtherValue, getTagsError, OTHER_ID } from './tags-field-utils';
import { FIELD_ERROR_CLASS, useError } from '../../field-error';
import { FIELD_TYPES } from '../../constants';
import { classes } from './tags-field-wut.st.css';
import classNames from 'classnames';
import { useTpaStyleParams } from '../shared/use-tpa-style-params';
import Fieldset from '../fieldset';
import BoxSelectionItem from '../box-selection-item';
import { ForcedState, useForcedState } from '../../contexts/forced-state-context';
import { useTranslation } from '../../contexts/translation-context';
import { NUMBER_OF_COLUMNS, OptionsLayout } from '../options-layout';
export const TagsFieldWUT = _ref => {
  let {
    id,
    target,
    label,
    hideLabel = false,
    customErrors,
    required = false,
    description,
    error,
    addOther,
    addOtherLabel,
    addOtherPlaceholder,
    value = null,
    fieldType = FIELD_TYPES.TAGS,
    options,
    validation,
    numberOfColumns = NUMBER_OF_COLUMNS.FREE_FLOW,
    otherInputCharacterLimit,
    disabled = false,
    onChange,
    onFocus,
    onBlur,
    FieldLayout,
    requiredIndicatorType,
    requiredIndicatorPlacement
  } = _ref;
  const {
    labelId,
    inputId,
    errorId,
    descriptionId,
    ariaDescribedBy
  } = useFieldAttributes(id, error, description);
  const {
    isBorderStyleBottom,
    isBorderStyleNone
  } = useTpaStyleParams();
  const _error = getTagsError(validation, error);
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  const {
    hasError,
    errorMessage
  } = useError(_error, customErrors, fieldType);
  const isFreeFlow = numberOfColumns === NUMBER_OF_COLUMNS.FREE_FLOW;
  const _value = useMemo(() => value || [], [value]);
  const _options = useMemo(() => options.map(option => ({
    title: option.label,
    value: option.value ?? option.label
  })), [options]);
  const handleChange = useCallback(optionValue => {
    const newValue = _value.includes(optionValue) ? _value.filter(val => val !== optionValue) : [..._value, optionValue];
    onChange == null || onChange(newValue);
  }, [_value, onChange]);
  const optionsNames = useMemo(() => options.map(option => option.label), [options]);
  const isOtherOptionSelected = _value.length > 0 && _value.some(val => !optionsNames.includes(val));
  const [otherSelected, setOtherSelected] = useState(isOtherOptionSelected);
  const [otherValue, setOtherValue] = useState(isOtherOptionSelected ? getOtherValue(_value, addOtherLabel) : '');
  const {
    t
  } = useTranslation();
  const otherLabel = addOtherLabel || (t == null ? void 0 : t('add-other.default-other-option-label')) || OTHER_ID;
  const otherOptionChange = () => {
    const _otherSelected = !otherSelected;
    setOtherSelected(_otherSelected);
    setOtherValue('');
    handleOtherChange(_otherSelected ? `${otherLabel}` : '');
  };
  const handleOtherChange = useCallback(newValue => {
    const filteredValues = _value.filter(val => optionsNames.includes(val));
    const updatedValues = newValue ? [...filteredValues, newValue] : filteredValues;
    onChange(updatedValues);
  }, [onChange, optionsNames, _value]);
  const handleOtherInputChange = event => {
    const input = event.target.value;
    const _otherValue = input ? `${addOtherLabel}: ${input}` : otherLabel;
    setOtherValue(input);
    handleOtherChange(_otherValue);
  };
  return /*#__PURE__*/React.createElement(FieldLayout, {
    fieldId: id,
    renderLabel: () => /*#__PURE__*/React.createElement(FieldLabelWUT, {
      labelId: labelId,
      htmlFor: inputId,
      required: required,
      label: label,
      hideLabel: hideLabel,
      requiredIndicatorType: requiredIndicatorType,
      requiredIndicatorPlacement: requiredIndicatorPlacement
    }),
    renderInput: () => /*#__PURE__*/React.createElement(Fieldset, {
      id: inputId,
      ariaLabelledBy: labelId,
      required: required,
      onFocus: onFocus,
      onBlur: onBlur,
      ariaDescribedBy: ariaDescribedBy
    }, options.length > 0 && /*#__PURE__*/React.createElement(OptionsLayout, {
      numberOfColumns: numberOfColumns,
      tabIndex: -1
    }, _options.map(_ref2 => {
      let {
        title: optionTitle,
        value: optionValue
      } = _ref2;
      const isChecked = _value.includes(optionTitle);
      return /*#__PURE__*/React.createElement(BoxSelectionItem, {
        id: optionValue,
        name: optionValue,
        size: BoxSelectionBoxSize.small,
        disabled: disabled,
        type: BoxSelectionSelectionType.CHECKBOX,
        key: `tags-${optionValue}`,
        "data-hook": `tags-${optionValue}`,
        checked: isChecked,
        onChange: event => {
          handleChange(event.id);
        },
        className: classNames({
          [FIELD_ERROR_CLASS]: hasError,
          [classes.tag]: isFreeFlow
        }),
        isBorderStyleBottom: isBorderStyleBottom,
        isBorderStyleNone: isBorderStyleNone,
        forcedHover: forcedHover,
        content: optionTitle
      });
    }), addOther && /*#__PURE__*/React.createElement(BoxSelectionItem, {
      id: `tags-${OTHER_ID}`,
      name: label,
      size: BoxSelectionBoxSize.small,
      disabled: disabled,
      type: BoxSelectionSelectionType.CHECKBOX,
      checked: otherSelected,
      onChange: otherOptionChange,
      content: addOtherLabel || 'Other',
      className: classNames({
        [FIELD_ERROR_CLASS]: hasError,
        [classes.tag]: isFreeFlow
      }),
      isBorderStyleNone: isBorderStyleNone,
      isBorderStyleBottom: isBorderStyleBottom,
      forcedHover: forcedHover
    })), otherSelected && /*#__PURE__*/React.createElement(TPATextField, {
      className: classNames(classes.otherInput, {
        [classes.noBorder]: isBorderStyleNone,
        [classes.hover]: forcedHover,
        [classes.filled]: Boolean(otherValue)
      }),
      value: otherValue,
      placeholder: addOtherPlaceholder,
      maxLength: otherInputCharacterLimit,
      onChange: handleOtherInputChange,
      disabled: disabled,
      error: hasError,
      inputMode: "text",
      "data-hook": "tags-other-input"
    })),
    renderDescription: () => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FieldErrorWUT, {
      target: target,
      hasError: hasError,
      errorMessage: errorMessage,
      id: errorId
    }), /*#__PURE__*/React.createElement(FieldDescriptionWUT, {
      description: description,
      id: descriptionId
    }))
  });
};
//# sourceMappingURL=tags-field-wut.js.map